import GoBackMenu from '@/components/GoBackNavbar';
import HorizontalMenu from '@/components/HorizontalMenu';
import Loader from '@/components/Loader';
import ProgramCourseLesson from '@/components/ProgramCourseLesson';
import ReadMore from '@/components/ReadMore';
import SideMenu from '@/components/SideMenu';
import StudentRating from '@/components/StudentRating';
import Constants from '@/Constants';
import { useCertificate } from '@/hooks/certificate';
import { EntityType } from '@/models/EntityTypes';
import { ProgramListStudentViewRequest } from '@/models/ProgramListStudentViewRequest';
import { LMSEvents } from '@/services/AnalyticsService';
import { getAuth } from '@/services/IdentityService';
import { programService } from '@/services/ProgramService';
import { getFormatTimeHourMin } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { getFavoriteListRequest } from '@/store/favorite/actions';
import {
    clearProgramClassStudentView,
    getProgramClassStudentViewRequest,
    getProgramClear,
    increaseProgramViewsRequest,
} from '@/store/program/action';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { DownloadCertificate } from '../LMS/Course/DownloadCertificate';
import './style.scss';

const ProgramPage: React.FC = () => {
    const programId: number = parseInt(useParams<{ id: string }>().id);
    const classEnrollmentId: number = parseInt(useParams<{ enrollmentId: string }>().enrollmentId);
    const dispatch = useDispatch();
    const history = useHistory();

    const { isExpired, program, person, currentProgramClassStudentView, classEnrollment, courseStudentViews, loading, strings } = useSelector(
        (state: ApplicationState) => {
            const { program } = state;
            const currentProgramClassStudentView = program.currentProgramClassStudentView;
            const { classEnrollment, courseStudentViews } = currentProgramClassStudentView;

            return {
                finishDate: currentProgramClassStudentView.classEnrollment?.programClass?.finishDate,
                isExpired: currentProgramClassStudentView.classEnrollment?.programClass?.isExpired,
                program: program.programData,
                currentProgramClassStudentView,
                classEnrollment,
                courseStudentViews,
                person: state.identity.data.person,
                loading: state.program.loading,
                strings: state.locale.data,
            };
        },
    );
    const { loadingCertificate, downloadCertificateFile } = useCertificate();

    const programWorkload =
        courseStudentViews && classEnrollment?.programClass && programService.getProgramWorkload(courseStudentViews, classEnrollment.programClass);
    const programClass = classEnrollment && classEnrollment.programClass;
    const programClassId = programClass ? programClass.id : 0;
    const hasCertificate = programClass && programClass.hasProgramCertification && programClass.program.type === Constants.programTypes.READY;
    const showCertificate =
        Boolean(classEnrollment && courseStudentViews) && programService.canItShowCertificate(classEnrollment, courseStudentViews);

    const onClickDownloadCertificate = () => {
        const auth = (getAuth() || '').replace('bearer ', '');
        const url = programService.certificateUrl(person, classEnrollment, auth);
        const filename = `${person.firstName}  ${person.lastName || ''} - ${classEnrollment.programClass.name}  - ${strings.CERTIFICATE} .pdf`;

        downloadCertificateFile(url, filename, 'application/pdf');
    };

    useEffect(() => {
        return () => {
            dispatch(clearProgramClassStudentView());
        };
    }, [dispatch]);

    useEffect(() => {
        if (programId) {
            dispatch(
                getProgramClassStudentViewRequest({
                    personId: person.id,
                    classEnrollmentId: classEnrollmentId,
                    programId: programId,
                } as ProgramListStudentViewRequest),
            );
            dispatch(getFavoriteListRequest());
            dispatch(increaseProgramViewsRequest(programId));
        } else {
            dispatch(getProgramClear());
        }
    }, [strings._LANGUAGE, programId]);

    const goBackPath = () => {
        const locationState = history.location.state;

        if (!locationState) {
            return '/';
        }

        switch (locationState) {
            case 'search':
                return '/search-result?fromProgram=true';
            case 'completed':
                return '/mine/completed';
            case 'trainings':
                return '/trainings/';
        }
    };

    useEffect(() => {
        if (isExpired && classEnrollment?.status !== Constants.classEnrollmentStatus.COMPLETED) {
            history.push('/');
        }
    }, [isExpired, classEnrollment?.status]);

    return (
        <>
            {loading && (
                <div className="d-flex flex-grow-1">
                    <Loader />
                </div>
            )}
            {!loading && (
                <div className="lms-program-page">
                    <GoBackMenu goBackPath={goBackPath()} />

                    <div className="list-bg" id={Constants.TOP_IMG_ID}>
                        <img className="list-bg__img" src={program.mainImage} />
                        <div className="container list-bg__container">
                            <div className="list-bg__corner col-sm-10"></div>
                        </div>
                    </div>

                    <div className="container">
                        <div className="row">
                            <div className="col-sm-8 offset-sm-2 list-page__adjust">
                                <div className="list-content">
                                    <h1 className="list-content__title">{program.name}</h1>

                                    <div className="list-content__info">
                                        <div className="list-content__status">
                                            <span>
                                                {program.author}
                                                <i className="material-icons align-middle separator">{Constants.icons.CIRCLE}</i>
                                                {classEnrollment && (
                                                    <span>{programService.getProgramStatusLabel(classEnrollment.status, strings)}</span>
                                                )}
                                            </span>
                                            {programClass && <span>{`${strings.CLASS_ID} #${programClassId}`}</span>}
                                        </div>
                                    </div>

                                    <HorizontalMenu
                                        entity={{ id: program.id as number, type: EntityType.PROGRAM, name: program.name }}
                                        hasFavorite
                                        moduleId={Constants.LMS_MODULE_ID}
                                    />

                                    {program.description && <ReadMore primaryColor={true} value={program.description} />}
                                    <div className="list-content__duration mb-0">
                                        {`${
                                            currentProgramClassStudentView.courseStudentViews
                                                ? currentProgramClassStudentView.courseStudentViews.length
                                                : 0
                                        }
                                        ${
                                            currentProgramClassStudentView.courseStudentViews &&
                                            currentProgramClassStudentView.courseStudentViews.length === 1
                                                ? strings.COURSE
                                                : strings.COURSES
                                        }`}
                                        <i className="material-icons align-middle separator">{Constants.icons.CIRCLE}</i>
                                        {getFormatTimeHourMin(programWorkload)}
                                        {hasCertificate && (
                                            <>
                                                <i className="material-icons align-middle separator">{Constants.icons.CIRCLE}</i>

                                                {strings.WITH_CERTIFICATE}
                                            </>
                                        )}
                                    </div>

                                    <ProgramCourseLesson />
                                </div>

                                {showCertificate && (
                                    <DownloadCertificate
                                        loading={loadingCertificate}
                                        onClick={() => onClickDownloadCertificate()}
                                        title={strings.COMPLETED_PROGRAM_MESSAGE}
                                    />
                                )}
                            </div>
                            <SideMenu entityType={EntityType.PROGRAM} isMenuFixed rating />
                        </div>
                    </div>
                    <StudentRating
                        analyticEvent={LMSEvents.RATE_PROGRAM}
                        classification
                        entityId={program.id}
                        entityName={program.name}
                        entityType={Constants.entityTypes.PROGRAM}
                        title={strings.LABEL_RATING_PROGRAM}
                    />
                </div>
            )}
        </>
    );
};

export default ProgramPage;
